import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OfficialReceipt from "../OfficialReceipt";
import { useTransactionReceipt } from "../../context/TransactionContext";
import { useDesign } from "../../context/DesignContext";
import { useAuth } from "../../context/AuthContext";

const pick = (...values) =>
    values.find(
        (value) =>
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ""
    );

export default function TransactionReceiptModal() {
    const { receipt, closeTransactionReceipt } = useTransactionReceipt();
    const { design } = useDesign();
    const { role } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [ready, setReady] = useState(false);

    const closeAndReturnHome = () => {
        closeTransactionReceipt();
        const normalizedRole = String(role || "").toLowerCase().replace(/[\s-]+/g, "_");
        if (["client", "ht_agent", "agent_ht"].includes(normalizedRole) && location.pathname !== "/dashboard") {
            // Apre kliyan an fin wè resi a, retire paj operasyon an nan history
            // pou Back pa resoumèt menm transfè/depo/retrè a pa aksidan.
            navigate("/dashboard", { replace: true });
        }
    };

    const receiptKey = useMemo(() => {
        if (!receipt) return "";
        const raw = receipt?.raw || receipt;
        return String(
            receipt.reference ||
            receipt.id ||
            raw.reference ||
            raw.id ||
            Date.now()
        );
    }, [receipt]);

    const delay = Math.max(
        0,
        Math.min(2000, Number(design?.receipts?.presentationDelayMs ?? 420))
    );

    useEffect(() => {
        if (!receipt) {
            setReady(false);
            return undefined;
        }
        setReady(false);
        const timer = window.setTimeout(() => setReady(true), delay);
        return () => window.clearTimeout(timer);
    }, [receiptKey, receipt, delay]);

    if (!receipt) return null;

    if (!ready && design?.receipts?.showPreparingState !== false) {
        return (
            <div className="mk-receipt-preparing-overlay" role="status" aria-live="polite">
                <div className="mk-receipt-preparing-card">
                    <div className="mk-receipt-preparing-logo">MK<span>TRANSFÈ</span></div>
                    <div className="mk-receipt-preparing-line" />
                    <div className="mk-receipt-preparing-title">N ap prepare resi ou...</div>
                    <div className="mk-receipt-preparing-subtitle">Yon ti moman</div>
                </div>
            </div>
        );
    }

    const raw = receipt?.raw || receipt;

    return (
        <OfficialReceipt
            templateId={pick(
                receipt.receiptTemplateUsed,
                design?.receipts?.activeTemplate,
                "RECEIPT_01"
            )}
            animationDurationMs={Number(design?.receipts?.animationDurationMs ?? 780)}
            service={pick(
                receipt.service,
                receipt.methodName,
                raw.service,
                raw.serviceName,
                raw.methodName,
                "MKTRANSFE"
            )}
            status={pick(
                receipt.status,
                raw.status,
                raw.providerStatus,
                "PENDING"
            )}
            reference={pick(
                receipt.reference,
                receipt.id,
                raw.reference,
                raw.paymentReference,
                raw.transferId,
                raw.depositId,
                raw.withdrawalId,
                raw.transactionId,
                raw.id,
                ""
            )}
            number={pick(
                receipt.number,
                raw.beneficiaryPhone,
                raw.phone,
                raw.phoneNumber,
                raw.accountNumber,
                raw.target,
                ""
            )}
            beneficiary={pick(
                receipt.beneficiary,
                raw.beneficiaryName,
                raw.accountName,
                raw.recipientName,
                ""
            )}
            amount={pick(receipt.amount, raw.amount, raw.netAmount, null)}
            currency={pick(receipt.currency, raw.currency, raw.walletCurrency, "")}
            fee={pick(receipt.fee, raw.fee, raw.bankFee, null)}
            total={pick(
                receipt.total,
                raw.total,
                raw.totalDebit,
                raw.totalDebited,
                raw.expectedReceiptTotal,
                null
            )}
            totalCurrency={pick(
                receipt.totalCurrency,
                raw.totalCurrency,
                raw.debitCurrency,
                receipt.currency,
                ""
            )}
            receiveAmount={pick(
                receipt.receiveAmount,
                raw.receiveAmount,
                raw.creditedAmount,
                raw.creditAmount,
                raw.gourdes,
                null
            )}
            receiveCurrency={pick(
                receipt.receiveCurrency,
                raw.receiveCurrency,
                raw.creditCurrency,
                raw.walletCurrency,
                ""
            )}
            exchangeRate={pick(
                receipt.exchangeRate,
                raw.exchangeRate,
                raw.rate,
                raw.conversionRate,
                null
            )}
            product={pick(receipt.product, raw.productName, raw.product, "")}
            plan={pick(receipt.plan, raw.planName, raw.plan, "")}
            createdAt={pick(receipt.createdAt, raw.createdAt, raw.created_at, null)}
            extraRows={Array.isArray(receipt.extraRows) ? receipt.extraRows : []}
            message={pick(receipt.message, raw.message, "")}
            onClose={closeAndReturnHome}
        />
    );
}
